import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getNotifications,
  getRecentNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '../api/notifications-api';

export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (page: number, pageSize: number) => [...notificationKeys.lists(), page, pageSize] as const,
  recent: () => [...notificationKeys.all, 'recent'] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

export const useNotifications = (page = 1, pageSize = 20) =>
  useQuery({
    queryKey: notificationKeys.list(page, pageSize),
    queryFn: () => getNotifications(page, pageSize),
    staleTime: 30_000,
  });

export const useRecentNotifications = () =>
  useQuery({
    queryKey: notificationKeys.recent(),
    queryFn: getRecentNotifications,
    staleTime: 30_000,
  });

export const useUnreadNotificationCount = () =>
  useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: getUnreadNotificationCount,
    staleTime: 30_000,
  });

export const useNotificationMutations = () => {
  const queryClient = useQueryClient();

  const invalidateNotifications = () => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  };

  const markRead = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: invalidateNotifications,
  });

  const markAllRead = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidateNotifications,
  });

  return {
    markRead,
    markAllRead,
  };
};
